<template>
  <div>
    <popup2 :value="!!event" @input="event = null" width="428">
      <edit-field
        v-if="event === 'addGroup'"
        :type="'chips'"
        :title="'Add device to groups'"
        :field="'Groups'"
        :value="[]"
      />
      <default-confirmation
        text-delete="Delete these objects completely?"
        v-else-if="event === 'delete'"
        @close="event = null"
        @delete="deleteAction"
      />
      <component
        v-else-if="event"
        :entity="{}"
        :is="event"
        :loading="loading"
        entity-type="DEVICE"
        type="DEVICE"
        :callback-after="load"
        @save="saveHandler"
        @close="event = null"
      />
    </popup2>
    <table-page
      :toolbar="toolbar"
      :table="table"
      :entity="true"
      :selected-out="eValue"
      @event="eventHandler"
    />
  </div>
</template>
<script>
import DefaultConfirmation from '@/components/app-entity/modals/DefaultConfirmation';
import pageActions from './pageActions';
import selectedActions from './selectedActions';
import {
  ref,
  computed,
  onMounted,
  watch,
  reactive,
  toRefs,
  onUnmounted
} from '@vue/composition-api';
import { nanoid } from 'nanoid';
import useDevices from '@/api/devices';
import useSettings from '@/api/tableSettings';
import toSnakeCase from 'lodash.snakecase';
import useSchemas from '@/api/schemas';
import useGroups from '@/api/groups';
import useMedia from '@/compositions/useMedia';
import useAuth from '@/api/auth';
import useCommonModal from '@/compositions/useCommonModal';
import { RELOAD_INTERVAL_SEC } from '@/utils/constants';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'devices',
  metaInfo: {
    titleTemplate: '%s | Devices'
  },
  components: {
    DefaultConfirmation,
    addNew: () => import('@/components/app-entity/modals/CreateObject'),
    changeSchema: () => import('@/components/app-entity/modals/EditScheme'),
    importDevices: () => import('@/components/app-entity/modals/ImportDevices.vue')
  },
  setup(props, { root }) {
    const { load: loadSettings, save: saveSettings } = useSettings();
    const { devices: devicesApi } = useDevices();
    const { schemas: schemasApi } = new useSchemas();
    const { groups: pureGroupsApi } = new useGroups();
    const { computeIcon } = useMedia();
    const modalComposition = useCommonModal();

    const { profile } = useAuth();
    let timerId = null;
    const {
      total,
      list,
      load: loadList,
      params,
      loading,
      setParams
    } = devicesApi;

    const visibleColumns = ref([]);
    const allColumns = ref([
      'name',
      'favourite',
      'muted',
      'enabled',
      'deviceBatteryLevel',
      'deviceResponseStatus',
      'deviceEmulation',
      'drivername',
      'editorgroup',
      'usergroup',
      'readergroup'
    ]);
    const showOnMobile = ['favourite', 'enabled'];

    watch(
      () => root.$route.query.filterTable,
      () => {
        load();
      }
    );

    function load(filterOuter = {}) {
      let filter = {
        ...params.value.filter,
        ...filterOuter,
        schemaType: { equalTo: 'device' }
      };

      if (root.$route.query.filterTable !== undefined) {
        filter.name = {
          includesInsensitive: root.$route.query.filterTable
        };
      }

      setParams({ filter });

      return loadList();
    }

    const event = ref(null);
    const bufferSelected = ref([]);
    const toolbar = ref({
      title: 'devices',
      showAccess: true,
      pageActions,
      selectedActions
    });

    const settings = computed(() => {
      return visibleColumns.value;
    });

    const columns = computed(() => {
      return allColumns.value.map(item => ({
        key: item,
        text: root.$t(`object.${item}`),
        value: item,
        showOnMobile: showOnMobile.includes(item)
      }));
    });
    const items = computed(() =>
      list.value.map(i => ({
        ...i,
        drivername: i.drivername,
        iconFallback: 'mdi-memory',
        avatar: computeIcon(i.schema)
      }))
    );
    const count = computed(() => total.value);
    const isLoading = computed(() => loading.value);
    const eValue = ref([]);
    const state = reactive({
      schemasList: [],
      usersGroup: [],
      table: {
        filters: [],
        total: count,
        items,
        columns,
        settings,
        loading: isLoading,
        itemRoute: 'devicesTableComponent'
      }
    });

    function deleteAction() {
      devicesApi.remove(eValue.value.map(i => ({ id: i.id }))).then(() => {
        load();
        eValue.value = [];
        event.value = null;
      });
    }

    function eventHandler({ event: e, data }) {
      eValue.value = data;
      switch (e) {
        case 'setSettings': {
          visibleColumns.value = data;
          saveSettings('devicesSettings', data);
          break;
        }
        case 'setFilters': {
          state.table.filters = cloneDeep(data);
          let filter = {
            schemaType: { equalTo: 'device' }
          };
          data
            .filter(i => {
              if (Array.isArray(i.model) && i.model.length > 0) {
                return true;
              } else if (!Array.isArray(i.model) && i.model.value !== null) {
                return true;
              }
              return false;
            })
            .forEach(i => {
              filter = {
                ...filter,
                ...i?.getFilter()
              };
            });
          setParams({ filter, offset: 0 });
          load(filter);
          break;
        }
        case 'delete': {
          event.value = 'delete';
          break;
        }
        case 'addNew': {
          event.value = 'addNew';
          break;
        }
        case 'importDevices': {
          modalComposition.modal.show({
            component: () =>
              import('@/components/app-entity/modals/AppDialog.vue'),
            bind: {
              nameModal: 'ImportDevices'
            },
            slots: {
              default: {
                component: () =>
                  import('@/components/app-entity/modals/ImportDevices.vue'),
                bind: {
                  entity: null
                },
                on: {
                  close() {
                    modalComposition.modal.hide('ImportDevices');
                  },
                  save(e) {
                  }
                }
              }
            }
          })
          break;
        }
        case 'changeSchema': {
          bufferSelected.value = data;
          event.value = 'changeSchema';
          break;
        }
        case 'massiveDisable': {
          devicesApi
            .update(eValue.value.map(i => ({ id: i.id, enabled: false })))
            .then(() => {
              load();
              eValue.value = [];
              event.value = null;
            });
          break;
        }
        case 'massiveEnable': {
          devicesApi
            .update(eValue.value.map(i => ({ id: i.id, enabled: true })))
            .then(() => {
              load();
              eValue.value = [];
              event.value = null;
            });
          break;
        }
        case 'massiveUnmute': {
          devicesApi
            .update(eValue.value.map(i => ({ id: i.id, muted: false })))
            .then(() => {
              load();
              eValue.value = [];
              event.value = null;
            });
          break;
        }
        case 'massiveMute': {
          devicesApi
            .update(eValue.value.map(i => ({ id: i.id, muted: true })))
            .then(() => {
              load();
              eValue.value = [];
              event.value = null;
            });
          break;
        }
        case 'massiveAccessRights': {
          modalComposition.modal.show({
            component: () =>
              import('@/components/app-entity/modals/AppDialog.vue'),
            bind: {
              nameModal: 'EditRightsGroup'
            },
            slots: {
              default: {
                component: () =>
                  import('@/components/app-entity/modals/EditRightsGroup'),
                bind: {
                  entity: null
                },
                on: {
                  close() {
                    modalComposition.modal.hide('EditRightsGroup');
                  },
                  save(e) {
                    const payload = {};

                    if (e.editorgroup) {
                      payload.editorgroup = e.editorgroup;
                    }
                    if (e.readergroup) {
                      payload.readergroup = e.readergroup;
                    }
                    if (e.usergroup) {
                      payload.usergroup = e.usergroup;
                    }

                    devicesApi
                      .update(
                        eValue.value.map(i => ({
                          id: i.id,
                          ...payload
                        }))
                      )
                      .then(() => {
                        load();
                        eValue.value = [];
                        event.value = null;
                      });
                    modalComposition.modal.hide('EditRightsGroup');
                  }
                }
              }
            }
          });
          break;
        }
        case 'addGroup': {
          event.value = 'addGroup';
          break;
        }
        case 'filteringTable': {
          const result = {};

          if (data.itemsPerPage) {
            result.first = data.itemsPerPage;
          }

          result.offset = (data.page - 1) * data.itemsPerPage;

          if (data.desc) {
            setParams({ desc: data.desc.length ? data.desc[0] : false });
          }

          if (data.sort) {
            if (data.sort.length) {
              setParams({ sort: data.sort[0] });
            } else {
              setParams({ sort: null });
              setParams({ orderBy: 'NATURAL' });
            }
          }

          if (data.desc && params.value.sort) {
            let sort = params.value.sort;
            if (params.value.sort === 'drivername') {
              sort = 'schemaName';
            }
            result.orderBy = toSnakeCase(sort).toUpperCase();
            result.orderBy += `_${data.desc[0] ? 'DESC' : 'ASC'}`;
          }
          setParams(result);
          load();
          break;
        }
      }
    }

    function saveHandler(data) {
      switch (event.value) {
        case 'addNew': {
          devicesApi
            .save({
              object: {
                schemaId: data.schema,
                name: data.name,
                enabled: data.enabled,
                description: data.description,
                editorgroup: data.editorgroup
              }
            })
            .then(r => {
              event.value = null;
              root.$router.push({
                name: 'devicesTableComponent',
                params: { id: r.data.createObject.object.id }
              });
              load();
            });
          break;
        }
        case 'changeSchema': {
          devicesApi
            .update(
              bufferSelected.value.map(item => ({
                id: item.id,
                schemaId: data
              }))
            )
            .then(r => {
              event.value = null;
              load();
            });
          break;
        }
      }
    }

    onUnmounted(() => {
      clearInterval(timerId);
      setParams(null);
    });

    onMounted(async () => {
      setParams({
        first:
          profile.value?.objectProperties.find(
            item => item.key === 'programNumbersOfRows'
          )?.value || 20,
        filter: {
          schemaType: { equalTo: 'device' },
          hardLinked: { equalTo: false }
        }
      });

      timerId = setInterval(load, RELOAD_INTERVAL_SEC);

      const setts = loadSettings('devicesSettings');

      if (setts) {
        visibleColumns.value = setts;
      } else {
        visibleColumns.value = allColumns.value;
      }

      await load();
      await schemasApi
        .loadPureSchemata('DEVICE', {
          objectsCount: {
            greaterThan: 0,
          },
          parentSchemaExists: false
        })
        .then(data => {
          state.schemasList = data.schemata.map(schema => ({
            id: schema.id,
            value: schema.id,
            name: schema.name
          }));
        });
      await pureGroupsApi.load().then(data => {
        state.usersGroup = data.userGroups.map(group => ({
          id: group.id,
          value: group.id,
          name: group.groupName
        }));
      });
      await root.$nextTick();
      const filters = [
        {
          id: 'favourite',
          title: 'Favorite',
          getFilter() {
            return {
              favourite: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          type: 'tags',
          values: [
            {
              value: true,
              name: 'Yes',
              id: nanoid()
            },
            {
              name: 'No',
              value: false,
              id: nanoid()
            }
          ]
        },
        {
          title: 'Muted',
          id: 'muted',
          type: 'tags',
          getFilter() {
            return {
              muted: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Yes', id: nanoid(), value: true },
            { name: 'No', id: nanoid(), value: false }
          ]
        },
        {
          id: 'enabled',
          title: 'Status',
          type: 'tags',
          getFilter() {
            return {
              enabled: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Enabled', id: nanoid(), value: true },
            { name: 'Disabled', id: nanoid(), value: false }
          ]
        },
        {
          id: 'response',
          title: 'Response status',
          getFilter() {
            return {
              deviceResponseStatus: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          type: 'tags',
          values: [
            { name: 'Active', id: nanoid(), value: true },
            { name: 'No response', id: nanoid(), value: false }
          ]
        },
        {
          id: 'emulation',
          disabled: true,
          getFilter() {
            return {
              deviceEmulation: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: {
            name: 'All',
            value: null
          },
          values: [
            { name: 'On', id: nanoid(), value: true },
            { name: 'Off', id: nanoid(), value: false },
            { name: 'n/a', id: nanoid(), value: null }
          ],
          title: 'Emulation'
        },
        {
          id: 'battery-type',
          disabled: true,
          model: [],
          type: 'tags',
          values: [
            { name: 'Battery', value: true, id: nanoid() },
            { name: 'DC', value: false, id: nanoid() },
            { name: 'n/a', value: null, id: nanoid() }
          ],
          title: 'Battery type'
        },
        {
          getFilter() {
            return {
              schemaName: {
                in: this.model.map(item => item.name)
              }
            };
          },
          id: 'Schemas name',
          model: [],
          type: 'tags',
          values: state.schemasList,
          title: 'Schema name'
        },
        {
          getFilter() {
            return {
              editorgroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Editors group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Editors group'
        },
        {
          getFilter() {
            return {
              usergroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Users group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Users group'
        },
        {
          getFilter() {
            return {
              readergroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Readers group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Readers group'
        }
      ];
      state.table.filters = cloneDeep(filters);
      state.table.emptyFilters = cloneDeep(filters);
    });
    return {
      ...toRefs(state),
      event,
      toolbar,
      eventHandler,
      saveHandler,
      deleteAction,
      settings,
      eValue,
      load,
      loading: false
    };
  }
};
</script>
